import { ButtonType, DialogPositioningBehavior, DialogType } from '@tableau/widgets';
import React from 'react';
import _ from 'underscore';

import { dialogBodyStyle } from '~src/catalog/styles/ExternalContentActionsStyles';
import { Formatters, Messages } from '~src/codegen/Localize';
import { ButtonWithSpinner } from '~src/common/react/components/ButtonWithSpinner';
import { ActionHelpers, standardActionErrorMsg, standardActionSuccessMsg, StandardActionToastMessages } from '~src/commonInfra/contentActions/ActionHelpers';
import { IErrorItem } from '~src/commonInfra/contentActions/MultipleItemResult';
import { ActionButtonLeftWide,ActionButtons } from '~src/commonInfra/dialogs/ReactActionDialog';
import { ReactModalService } from '~src/commonInfra/dialogs/ReactModalService';
import { TabDialogWidth } from '~src/commonInfra/dialogs/style/TabDialogWidth';
import { IResult } from '~src/httpClient/serverApi/types/IResponse';
import { L10n } from '~src/ts/L10n/L10n';
import { Set } from '~src/ts/models/Set';
import { IWorkbookBase } from '~src/workbook/models/IWorkbookInfo';
import { Workbooks } from '~src/workbook/resources/Workbooks';
import { WorkbookErrors } from '~src/workbook/services/WorkbookErrors';

export namespace SetDisplayTabsAction {

  export interface SetDisplayTabsFooterProps {
    closeDialog: () => void;
    dismissDialog: () => void;
    showAllTabs: () => void;
    hideAllTabs: () => void;
    hideTabsDisabled: boolean;
    showTabsDisabled: boolean;
  }

  export const SetDisplayTabsFooter = (props: SetDisplayTabsFooterProps): JSX.Element => {
    const [showTabsBusy, setShowTabsBusy] = React.useState<boolean>(false);
    const [hideTabsBusy, setHideTabsBusy] = React.useState<boolean>(false);

    const showAllTabs = React.useCallback(
      async () => {
        if (props.showTabsDisabled) {
          return;
        }
        setShowTabsBusy(true);
        await props.showAllTabs();
        props.closeDialog();

      },
      // This deps array was noncompliant before we enabled the lint rule. If you’re here, please consider fixing it! See TFS 1257824
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [setShowTabsBusy, props.showAllTabs, props.showTabsDisabled, props.closeDialog]
    );

    const hideAllTabs = React.useCallback(
      async () => {
        if (props.hideTabsDisabled) {
          return;
        }
        setHideTabsBusy(true);
        await props.hideAllTabs();
        props.closeDialog();

      },
      // This deps array was noncompliant before we enabled the lint rule. If you’re here, please consider fixing it! See TFS 1257824
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [setHideTabsBusy, props.hideAllTabs, props.hideTabsDisabled, props.closeDialog]
    );

    return <div className={ActionButtons} >
      <span className={ActionButtonLeftWide}>
        <ButtonWithSpinner
          buttonType={ButtonType.Primary}
          clicked={showAllTabs}
          testId={'confirm-show-tabs'}
          title={Messages.confirmSetDisplayTabsShowButton()}
          disabled={showTabsBusy || hideTabsBusy || props.showTabsDisabled}
          showSpinner={showTabsBusy}
        />
      </span>
      <span className={ActionButtonLeftWide}>
        <ButtonWithSpinner
          buttonType={ButtonType.Primary}
          clicked={hideAllTabs}
          testId={'confirm-hide-tabs'}
          title={Messages.confirmSetDisplayTabsHideButton()}
          disabled={showTabsBusy || hideTabsBusy || props.hideTabsDisabled}
          showSpinner={hideTabsBusy}
        />
      </span>
    </div>;
  };

  export function confirmSetDisplayTabs(workbooks: Set<IWorkbookBase>) {

    const workbookArray = workbooks.toArray();
    const anyTabsEnabled = _.any(workbookArray, (wb: IWorkbookBase) => wb.displayTabs);
    const anyTabsDisabled = _.any(workbookArray, (wb: IWorkbookBase) => !wb.displayTabs);

    const message = Messages.confirmSetDisplayTabsPrompt({
      ITEM_COUNT: workbooks.size(),
      ITEM_NAME: L10n.elideIfLong(workbooks.first().name),
      ITEM_COUNT_LOC: Formatters.formatCount(workbooks.size())
    });

    const modal = ReactModalService.open({
      title:  Messages.confirmSetDisplayTabsTitle(),
      type: DialogType.NormalWithTitleBar,
      showCloseButton: true,
      dismissDialogOnCloseAction: true,
      positioningBehavior: DialogPositioningBehavior.Centered,
      dialogContent: <>{message}</>,
      dialogBodyStyle: dialogBodyStyle,
      dialogWidth: TabDialogWidth.width500px,
      footerContent: <SetDisplayTabsFooter
        dismissDialog={() => modal.dismiss()}
        closeDialog={() => modal.close()}
        showAllTabs={() => setDisplayTabs(workbooks, true)}
        hideAllTabs={() => setDisplayTabs(workbooks, false)}
        showTabsDisabled={!anyTabsDisabled}
        hideTabsDisabled={!anyTabsEnabled} />
    });
    return Promise.resolve(modal.result);
  }

  function setDisplayTabs(workbooks: Set<IWorkbookBase>, displayTabs: boolean) {
    return Workbooks.setDisplayTabs(workbooks.keys(), displayTabs)
      .then((result: IResult) => {
        notifyResult(displayTabs, workbooks, result);
      });
  }

  function notifyResult(displayTabs: boolean, workbooks: Set<IWorkbookBase>, result: IResult) {

    const translations: StandardActionToastMessages = displayTabs ?
      {
        success: standardActionSuccessMsg(Messages.setDisplayTabsShowSuccess),
        error: standardActionErrorMsg(Messages.setDisplayTabsShowError),
      } : {
        success: standardActionSuccessMsg(Messages.setDisplayTabsHideSuccess),
        error: standardActionErrorMsg(Messages.setDisplayTabsHideError),
      };

    ActionHelpers.displayStandardToastsForResult(
      workbooks,
      result,
      translations,
      (errorItem: IErrorItem<IWorkbookBase>) => WorkbookErrors.errorDetailsFor(errorItem)
    );
  }
}

export type SetDisplayTabsAction = typeof SetDisplayTabsAction;
